// src/utils/userPrefs/UserPrefs.ts
import Cookies from "js-cookie";
import { User } from "../../types/admin/UserTypes";
import { applyThemeClasses } from "./themeUtils";
import { getSessionUser, updateSessionUser } from "../auth/sessionUser";
import { parsePreferences } from "./parsePreferences";

/**
 * Default user preferences to use when none are set
 */
export const DEFAULT_PREFERENCES = {
	// Theme preferences
	theme: "light", // light, dark
	accentColor: "blue", // blue, purple, green, teal, amber, pink

	// Interface preferences
	sidebarCollapsed: false,
	geneWheelRailCollapsed: false, // gene.codes wheel modal left rail
	tableRowsPerPage: 10,
	dateFormat: "DD/MM/YYYY",
	timeFormat: "24h", // 12h, 24h
	intrayScope: "mine", // mine, all — Results In Tray scope toggle
	xoeChatMode: "floating", // floating | fullpage | both

	// Notification preferences
	emailNotifications: true,
	smsNotifications: false,
	appointmentReminders: true,

	// Clinical preferences
	defaultMeasurementUnit: "metric", // metric, imperial
	defaultLabView: "timeline", // timeline, tabular
	patientNameFormat: "lastFirst", // lastFirst, firstLast
	storeUnverifiedIHI: true, // Whether to store unverified IHIs
};

/**
 * Type definition for user preferences
 */
export type UserPreferences = typeof DEFAULT_PREFERENCES;

/**
 * Type for valid accent colors
 */
export type AccentColor = 'blue' | 'purple' | 'green' | 'teal' | 'amber' | 'pink';

/**
 * Load user preferences from cookies, parse the JSON string, and merge with defaults
 */
export function loadUserPreferences(): UserPreferences {
	try {
		// First check for dedicated preferences cookie
		const preferencesString = Cookies.get("userPreferences");
		if (preferencesString && preferencesString !== "undefined" && preferencesString !== "null") {
			try {
				// Handle double-stringified JSON from the API
				let parsedPrefs = JSON.parse(preferencesString);
				if (typeof parsedPrefs === 'string') {
					parsedPrefs = JSON.parse(parsedPrefs);
				}
				
				// Ensure we have valid preferences object
				if (parsedPrefs && typeof parsedPrefs === 'object') {
					return { ...DEFAULT_PREFERENCES, ...parsedPrefs };
				}
			} catch (error) {
				console.error("Error parsing preferences cookie:", error);
				// Clear invalid cookie
				Cookies.remove("userPreferences");
			}
		}

		// Fall back to the session user if no separate preferences cookie
		try {
			const userPrefs = parsePreferences(getSessionUser().preferences);
			if (userPrefs) {
				// Set separate preferences cookie for faster access next time
				Cookies.set("userPreferences", JSON.stringify(userPrefs), { expires: 365 });
				return { ...DEFAULT_PREFERENCES, ...userPrefs };
			}
		} catch (error) {
			console.error("Error parsing user object:", error);
		}

		// Return defaults if all else fails
		return DEFAULT_PREFERENCES;
	} catch (error) {
		console.error("Error loading user preferences:", error);
		return DEFAULT_PREFERENCES;
	}
}

/**
 * Save user preferences to cookies by updating the user object
 */
export async function saveUserPreferences(preferences: Partial<UserPreferences>): Promise<boolean> {
	try {
		const user: User = getSessionUser();
		if (!user.id) {
			return false;
		}

		// Get current preferences or create empty object
		const currentPreferences = parsePreferences(user.preferences) ?? {};

		// Merge new preferences with current ones
		const updatedPreferences = { ...currentPreferences, ...preferences };

		updateSessionUser({ preferences: JSON.stringify(updatedPreferences) });

		// Apply theme-related preferences immediately
		if (preferences.theme || preferences.accentColor) {
			applyThemePreferences(updatedPreferences as UserPreferences);

			// Trigger a custom event so App can reload theme config
			window.dispatchEvent(new CustomEvent('themeChange', {
				detail: updatedPreferences
			}));
		}

		return true;
	} catch (error) {
		console.error("Error saving user preferences:", error);
		return false;
	}
}

/**
 * Apply theme-related preferences (CSS classes only)
 * The actual Ant Design theme is applied via ConfigProvider in App.tsx
 */
export function applyThemePreferences(preferences: UserPreferences): void {
	const themeMode = preferences.theme as 'light' | 'dark';
	applyThemeClasses(themeMode);
}

/**
 * Hook to get and set a specific preference
 * For use in React components
 */
export function usePreference<K extends keyof UserPreferences>(
	key: K
): [UserPreferences[K], (value: UserPreferences[K]) => Promise<boolean>] {
	const preferences = loadUserPreferences();
	const value = preferences[key];

	const setValue = async (newValue: UserPreferences[K]): Promise<boolean> => {
		return await saveUserPreferences({ [key]: newValue } as Partial<UserPreferences>);
	};

	return [value, setValue];
}

// ── Xoe chat surface preference ──────────────────────────────────────────────
// Kept together as one block: the floating widget (`XoeWidget`) and the nav
// entry (`Sidebar`) both read the mode through useXoeChatMode() below. Two
// independent usePreference("xoeChatMode") reads is how the two surfaces drift
// out of sync.
//
// ⚠️ **Corrected at X9/X10.** This used to name THREE readers — "the floating
// widget, the nav entry and the full-page tab" — in the present tense, while
// only the widget existed. There are two, and the third never becomes one: the
// PAGE at `/xoe` deliberately does NOT re-read the mode. Reaching the page means
// the nav entry rendered (or the practitioner typed the URL), and a page that
// second-guessed the preference it was navigated to would redirect the
// practitioner away from a link they had just followed. The mode chooses the
// ENTRY POINTS; the route is gated on the flag and the role, in lockstep with
// the nav entry (both read `canAccessXoe`).

/**
 * How the practitioner reaches Xoe. TWO surfaces exist: the floating edge tab
 * (XoeWidget) and a full-page route. `both` means both ENTRY POINTS to ONE
 * chat — never two independent chats with two turn states.
 */
export type XoeChatMode = "floating" | "fullpage" | "both";

/**
 * Whether the full-page Xoe route actually exists.
 *
 * The page is built separately; the change that mounts it flips this to `true`.
 * While it is false `fullpage` has nowhere to go, so resolveXoeChatMode() below
 * degrades it to `floating` — a preference that makes the assistant unreachable
 * is worse than one that is temporarily inert, and shipped-inert is a mistake
 * this repo has already made once.
 *
 * ⚠️ **FLIPPED TO `true` AT X9** — `XoeAssistantPage` is mounted at
 * `XOE_FULLPAGE_PATH` by `App.tsx`, so `fullpage` now resolves to itself. The
 * false branch is NOT dead: it is the withdrawal path. Pulling the page (a route
 * removed in a hotfix) means flipping this back, which degrades every `fullpage`
 * user to the floating tab instead of stranding them with a nav entry that has
 * gone and no other way in. `both` users are unaffected either way.
 *
 * It is read in exactly ONE place — resolveXoeChatMode() — and that is the whole
 * point of the constant. `Sidebar` and `App.tsx` gate on the flag, the role and
 * (for the nav entry) the resolved mode; neither reads this directly.
 */
export const XOE_FULLPAGE_ROUTE_AVAILABLE: boolean = true;

/**
 * The pure decision behind useXoeChatMode(), with route availability taken as a
 * PARAMETER rather than read from the constant above.
 *
 * That parameter is the only reason this function is separate: once
 * XOE_FULLPAGE_ROUTE_AVAILABLE is `true`, the degradation branch is unreachable
 * from the hook, and a constant cannot be stubbed per-test. Taking it as an
 * argument keeps the withdrawal path — the branch that decides what happens to a
 * `fullpage` user when the page is pulled — genuinely exercised rather than
 * merely present.
 *
 * `both` is deliberately NOT degraded: the floating tab still renders under it,
 * so the assistant stays reachable and there is nothing to repair.
 */
export function resolveXoeChatMode(
	stored: string | undefined,
	routeAvailable: boolean,
): XoeChatMode {
	if (stored === "both") return "both";
	if (stored === "fullpage") return routeAvailable ? "fullpage" : "floating";
	return "floating";
}

/**
 * The single read site for `xoeChatMode`. Returns the EFFECTIVE mode: an
 * unrecognised stored value falls back to `floating`, and so does `fullpage`
 * whenever XOE_FULLPAGE_ROUTE_AVAILABLE is false.
 *
 * ⚠️ Like usePreference() above, this is a hook by name only: it reads cookies
 * during render and subscribes to nothing. That is sufficient because the value
 * is only ever written from the preferences page (a different route), and every
 * caller re-renders on navigation. Do NOT build a subscription for it.
 */
export function useXoeChatMode(): XoeChatMode {
	return resolveXoeChatMode(loadUserPreferences().xoeChatMode, XOE_FULLPAGE_ROUTE_AVAILABLE);
}

/**
 * Legacy function - kept for compatibility
 * @deprecated Ant Design theme is now handled via ConfigProvider
 */
export function getPrimeReactTheme(theme: string, accentColor: string): string {
	console.warn('getPrimeReactTheme is deprecated');
	return `lara-${theme}-${accentColor}`;
}